import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

const THEMES = {
  light: {
    id: 'light',
    label: 'Classic Light',
    '--bg-page': '#f7f5ef',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#fbfaf8',
    '--bg-page-accent': 'radial-gradient(1200px 600px at -10% -10%, rgba(199,163,74,0.08) 0%, rgba(31,106,93,0.08) 35%, rgba(15,34,56,0.06) 70%)',
    '--text-primary': '#0f2238',
    '--text-muted': '#6a7180',
    '--text-soft': '#9aa3b2',
    '--border': '#ebe3d8',
    '--border-strong': '#e5ddd2',
    '--brand': '#0f2238',
    '--brand-contrast': '#f7f2e8',
    '--accent-gold': '#c7a34a',
    '--accent-emerald': '#1f6a5d',
    '--accent-violet': '#5b3d86',
    '--accent-amber': '#7a5a1b',
    '--card-user-bg': '#e8eef7',
    '--card-company-bg': '#e8f4f0',
    '--card-doc-bg': '#f2ecfb',
    '--card-comment-bg': '#fbf5e8',
    '--shadow': '0 10px 24px rgba(15, 34, 56, 0.08)',
    '--sidebar-bg': 'linear-gradient(180deg, #0f2238 0%, #162b46 100%)',
    '--sidebar-text': '#f4f7fb',
    '--sidebar-muted': '#c9d3e1',
    '--sidebar-active': 'rgba(199,163,74,0.16)',
    '--sidebar-card': 'rgba(255,255,255,0.06)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.1)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(245,248,252,0.46)',
    '--sidebar-accent': '#d8b56a',
    '--sidebar-accent-soft': 'rgba(216,181,106,0.18)',
    '--sidebar-accent-glow': 'rgba(216,181,106,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #d6b66f 0%, #a38137 100%)',
    '--sidebar-logout': '#ffd8d8',
    '--topbar-bg': 'linear-gradient(180deg, rgba(244,245,247,0.98) 0%, rgba(244,245,247,0.92) 72%, rgba(244,245,247,0.7) 100%)',
    '--topbar-border': '1px solid rgba(15,34,56,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #15324f 0%, #193e62 48%, #215a5a 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(15,23,42,0.16)',
    '--hero-text': '#f7fafc',
    '--hero-muted': 'rgba(247,250,252,0.82)',
    '--hero-soft': 'rgba(247,250,252,0.68)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.22)',
    '--overlay-bg': 'rgba(7,12,18,0.55)',
    '--neutral-tint': 'rgba(148,163,184,0.12)',
    '--neutral-soft': 'rgba(148,163,184,0.1)',
    '--neutral-border': 'rgba(148,163,184,0.12)',
    '--danger': '#c03434',
    '--danger-soft': 'rgba(192,52,52,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #f8fafc, #eef2f7)',
    '--module-navy-tint': 'rgba(19,49,79,0.08)',
    '--module-navy-text': '#16314f',
    '--module-navy-soft': '#edf3f9',
    '--module-navy-dark-tint': 'rgba(59,130,246,0.22)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(31,106,93,0.1)',
    '--module-emerald-text': '#1f6a5d',
    '--module-emerald-soft': '#edf7f4',
    '--module-emerald-dark-tint': 'rgba(16,185,129,0.22)',
    '--module-emerald-dark-text': '#d1fae5',
    '--module-violet-tint': 'rgba(94,77,161,0.1)',
    '--module-violet-text': '#5e4da1',
    '--module-violet-soft': '#f2effb',
    '--module-violet-dark-tint': 'rgba(167,139,250,0.22)',
    '--module-violet-dark-text': '#ede9fe',
    '--module-amber-tint': 'rgba(178,132,41,0.12)',
    '--module-amber-text': '#8b6320',
    '--module-amber-soft': '#fbf5e8',
    '--module-amber-dark-tint': 'rgba(245,158,11,0.22)',
    '--module-amber-dark-text': '#fef3c7',
  },
  dark: {
    id: 'dark',
    label: 'Midnight Slate',
    '--bg-page': '#0c1117',
    '--bg-surface': '#111821',
    '--bg-elev': '#0f151d',
    '--bg-page-accent': 'radial-gradient(1200px 600px at -10% -10%, rgba(210,178,103,0.12) 0%, rgba(79,179,154,0.08) 35%, rgba(127,176,255,0.08) 70%)',
    '--text-primary': '#e6edf6',
    '--text-muted': '#b5bfcc',
    '--text-soft': '#8f9aaa',
    '--border': '#1f2a36',
    '--border-strong': '#2a3442',
    '--brand': '#e6edf6',
    '--brand-contrast': '#0c1117',
    '--accent-gold': '#d2b267',
    '--accent-emerald': '#4fb39a',
    '--accent-violet': '#9b79cf',
    '--accent-amber': '#d6a45a',
    '--card-user-bg': 'rgba(230,237,246,0.08)',
    '--card-company-bg': 'rgba(79,179,154,0.14)',
    '--card-doc-bg': 'rgba(155,121,207,0.16)',
    '--card-comment-bg': 'rgba(214,164,90,0.16)',
    '--shadow': '0 14px 28px rgba(0,0,0,0.35)',
    '--sidebar-bg': 'linear-gradient(180deg, #0d1622 0%, #0b131d 100%)',
    '--sidebar-text': '#edf3fb',
    '--sidebar-muted': 'rgba(237,243,251,0.68)',
    '--sidebar-active': 'rgba(210,178,103,0.18)',
    '--sidebar-card': 'rgba(255,255,255,0.05)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.08)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(237,243,251,0.42)',
    '--sidebar-accent': '#7fb0ff',
    '--sidebar-accent-soft': 'rgba(127,176,255,0.18)',
    '--sidebar-accent-glow': 'rgba(127,176,255,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #6da8ff 0%, #3d6fbe 100%)',
    '--sidebar-logout': '#ffb2b2',
    '--topbar-bg': 'linear-gradient(180deg, rgba(13,22,34,0.98) 0%, rgba(11,19,29,0.92) 72%, rgba(11,19,29,0.72) 100%)',
    '--topbar-border': '1px solid rgba(255,255,255,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #142536 0%, #193246 48%, #1a4a49 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(0,0,0,0.28)',
    '--hero-text': '#f2f6fb',
    '--hero-muted': 'rgba(242,246,251,0.78)',
    '--hero-soft': 'rgba(242,246,251,0.62)',
    '--hero-role-bg': 'rgba(255,255,255,0.1)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.04) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.16)',
    '--overlay-bg': 'rgba(3,6,10,0.72)',
    '--neutral-tint': 'rgba(148,163,184,0.16)',
    '--neutral-soft': 'rgba(148,163,184,0.12)',
    '--neutral-border': 'rgba(148,163,184,0.18)',
    '--danger': '#ff7878',
    '--danger-soft': 'rgba(255,120,120,0.16)',
    '--danger-contrast': '#14090a',
    '--preview-bg': 'linear-gradient(180deg, #111923, #0d141d)',
    '--module-navy-tint': 'rgba(96,165,250,0.2)',
    '--module-navy-text': '#dbeafe',
    '--module-navy-soft': 'rgba(96,165,250,0.14)',
    '--module-navy-dark-tint': 'rgba(59,130,246,0.28)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(52,211,153,0.18)',
    '--module-emerald-text': '#d1fae5',
    '--module-emerald-soft': 'rgba(16,185,129,0.14)',
    '--module-emerald-dark-tint': 'rgba(16,185,129,0.28)',
    '--module-emerald-dark-text': '#d1fae5',
    '--module-violet-tint': 'rgba(167,139,250,0.18)',
    '--module-violet-text': '#ede9fe',
    '--module-violet-soft': 'rgba(139,92,246,0.14)',
    '--module-violet-dark-tint': 'rgba(167,139,250,0.26)',
    '--module-violet-dark-text': '#ede9fe',
    '--module-amber-tint': 'rgba(251,191,36,0.18)',
    '--module-amber-text': '#fef3c7',
    '--module-amber-soft': 'rgba(245,158,11,0.14)',
    '--module-amber-dark-tint': 'rgba(245,158,11,0.28)',
    '--module-amber-dark-text': '#fef3c7',
  },
  forest: {
    id: 'forest',
    label: 'Forest Ledger',
    '--bg-page': '#edf3ee',
    '--bg-surface': '#fbfdfb',
    '--bg-elev': '#f4f9f3',
    '--bg-page-accent': 'radial-gradient(900px 520px at 0% 0%, rgba(43,94,74,0.14) 0%, rgba(135,178,91,0.09) 42%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#183126',
    '--text-muted': '#5d6f63',
    '--text-soft': '#85968a',
    '--border': '#d8e4d7',
    '--border-strong': '#c7d9c5',
    '--brand': '#1e4b3a',
    '--brand-contrast': '#f3f8f1',
    '--accent-gold': '#a88a3a',
    '--accent-emerald': '#2f6a53',
    '--accent-violet': '#5f6b8f',
    '--accent-amber': '#8f6f1c',
    '--card-user-bg': '#e7f1eb',
    '--card-company-bg': '#e0efe6',
    '--card-doc-bg': '#edf0fb',
    '--card-comment-bg': '#f6f0dc',
    '--shadow': '0 12px 30px rgba(30,75,58,0.12)',
    '--sidebar-bg': 'linear-gradient(180deg, #1d4738 0%, #17382d 100%)',
    '--sidebar-text': '#edf6f0',
    '--sidebar-muted': 'rgba(237,246,240,0.76)',
    '--sidebar-active': 'rgba(168,138,58,0.18)',
    '--sidebar-card': 'rgba(255,255,255,0.07)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.11)',
    '--sidebar-line': 'rgba(255,255,255,0.09)',
    '--sidebar-faint': 'rgba(237,246,240,0.48)',
    '--sidebar-accent': '#c8b06c',
    '--sidebar-accent-soft': 'rgba(200,176,108,0.2)',
    '--sidebar-accent-glow': 'rgba(200,176,108,0.24)',
    '--sidebar-avatar': 'linear-gradient(135deg, #d8c27f 0%, #8f7330 100%)',
    '--sidebar-logout': '#ffd4d4',
    '--topbar-bg': 'linear-gradient(180deg, rgba(238,245,239,0.98) 0%, rgba(238,245,239,0.92) 72%, rgba(238,245,239,0.72) 100%)',
    '--topbar-border': '1px solid rgba(29,71,56,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #1f4b3c 0%, #295945 48%, #486233 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(30,75,58,0.22)',
    '--hero-text': '#f2f8f3',
    '--hero-muted': 'rgba(242,248,243,0.8)',
    '--hero-soft': 'rgba(242,248,243,0.64)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.06) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.18)',
    '--overlay-bg': 'rgba(9,18,14,0.58)',
    '--neutral-tint': 'rgba(107,126,118,0.12)',
    '--neutral-soft': 'rgba(107,126,118,0.1)',
    '--neutral-border': 'rgba(107,126,118,0.12)',
    '--danger': '#b74d4d',
    '--danger-soft': 'rgba(183,77,77,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #f4f8f3, #e8f0e6)',
    '--module-navy-tint': 'rgba(38,78,116,0.1)',
    '--module-navy-text': '#23496c',
    '--module-navy-soft': '#e8eff4',
    '--module-navy-dark-tint': 'rgba(78,138,188,0.22)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(47,106,83,0.12)',
    '--module-emerald-text': '#295945',
    '--module-emerald-soft': '#e6f0eb',
    '--module-emerald-dark-tint': 'rgba(83,176,141,0.22)',
    '--module-emerald-dark-text': '#dcfce7',
    '--module-violet-tint': 'rgba(95,107,143,0.12)',
    '--module-violet-text': '#576688',
    '--module-violet-soft': '#eceff6',
    '--module-violet-dark-tint': 'rgba(136,149,191,0.22)',
    '--module-violet-dark-text': '#ede9fe',
    '--module-amber-tint': 'rgba(143,111,28,0.12)',
    '--module-amber-text': '#86631e',
    '--module-amber-soft': '#f6f0dd',
    '--module-amber-dark-tint': 'rgba(200,163,80,0.22)',
    '--module-amber-dark-text': '#fef3c7',
  },
  rose: {
    id: 'rose',
    label: 'Rose Quartz',
    '--bg-page': '#fcf1f1',
    '--bg-surface': '#fffafa',
    '--bg-elev': '#fff4f5',
    '--bg-page-accent': 'radial-gradient(1000px 560px at -6% -8%, rgba(201,87,111,0.16) 0%, rgba(235,177,141,0.11) 36%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#402430',
    '--text-muted': '#7b6270',
    '--text-soft': '#a48b96',
    '--border': '#f0dbe0',
    '--border-strong': '#e6cbd2',
    '--brand': '#7d3651',
    '--brand-contrast': '#fff6f8',
    '--accent-gold': '#d38b68',
    '--accent-emerald': '#6f8f7d',
    '--accent-violet': '#8b5c77',
    '--accent-amber': '#b66e4f',
    '--card-user-bg': '#f9e6eb',
    '--card-company-bg': '#f6ece4',
    '--card-doc-bg': '#f2e8f5',
    '--card-comment-bg': '#fff0e7',
    '--shadow': '0 12px 30px rgba(125,54,81,0.14)',
    '--sidebar-bg': 'linear-gradient(180deg, #6b3047 0%, #4e2536 100%)',
    '--sidebar-text': '#fff0f4',
    '--sidebar-muted': 'rgba(255,240,244,0.74)',
    '--sidebar-active': 'rgba(211,139,104,0.18)',
    '--sidebar-card': 'rgba(255,255,255,0.08)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.13)',
    '--sidebar-line': 'rgba(255,255,255,0.1)',
    '--sidebar-faint': 'rgba(255,240,244,0.48)',
    '--sidebar-accent': '#efb28d',
    '--sidebar-accent-soft': 'rgba(239,178,141,0.2)',
    '--sidebar-accent-glow': 'rgba(239,178,141,0.26)',
    '--sidebar-avatar': 'linear-gradient(135deg, #f0bc9b 0%, #b86d55 100%)',
    '--sidebar-logout': '#ffd7de',
    '--topbar-bg': 'linear-gradient(180deg, rgba(252,241,241,0.98) 0%, rgba(252,241,241,0.92) 72%, rgba(252,241,241,0.72) 100%)',
    '--topbar-border': '1px solid rgba(107,48,71,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #7d3651 0%, #96506a 45%, #c27661 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(125,54,81,0.2)',
    '--hero-text': '#fff5f7',
    '--hero-muted': 'rgba(255,245,247,0.82)',
    '--hero-soft': 'rgba(255,245,247,0.68)',
    '--hero-role-bg': 'rgba(255,255,255,0.14)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(24,10,15,0.56)',
    '--neutral-tint': 'rgba(164,139,150,0.12)',
    '--neutral-soft': 'rgba(164,139,150,0.1)',
    '--neutral-border': 'rgba(164,139,150,0.12)',
    '--danger': '#c34b68',
    '--danger-soft': 'rgba(195,75,104,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #fff7f8, #f8ecef)',
    '--module-navy-tint': 'rgba(91,110,148,0.1)',
    '--module-navy-text': '#56698b',
    '--module-navy-soft': '#eef1f8',
    '--module-navy-dark-tint': 'rgba(116,138,191,0.22)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(111,143,125,0.12)',
    '--module-emerald-text': '#5b7968',
    '--module-emerald-soft': '#edf3ef',
    '--module-emerald-dark-tint': 'rgba(111,176,146,0.22)',
    '--module-emerald-dark-text': '#dcfce7',
    '--module-violet-tint': 'rgba(139,92,119,0.12)',
    '--module-violet-text': '#84566e',
    '--module-violet-soft': '#f4ecf1',
    '--module-violet-dark-tint': 'rgba(178,126,155,0.22)',
    '--module-violet-dark-text': '#f5e8f0',
    '--module-amber-tint': 'rgba(182,110,79,0.12)',
    '--module-amber-text': '#aa694d',
    '--module-amber-soft': '#fdf0e9',
    '--module-amber-dark-tint': 'rgba(223,155,114,0.22)',
    '--module-amber-dark-text': '#fff0e5',
  },
  ocean: {
    id: 'ocean',
    label: 'Ocean Ink',
    '--bg-page': '#eef5fb',
    '--bg-surface': '#fbfdff',
    '--bg-elev': '#f3f9fe',
    '--bg-page-accent': 'radial-gradient(980px 540px at -8% -10%, rgba(27,89,146,0.16) 0%, rgba(74,163,213,0.12) 35%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#17314f',
    '--text-muted': '#5e7690',
    '--text-soft': '#8ba1b6',
    '--border': '#d9e6f1',
    '--border-strong': '#c8dbea',
    '--brand': '#1f5184',
    '--brand-contrast': '#f4f9ff',
    '--accent-gold': '#d39c52',
    '--accent-emerald': '#2d7f7a',
    '--accent-violet': '#556fb0',
    '--accent-amber': '#ad7730',
    '--card-user-bg': '#e2eef9',
    '--card-company-bg': '#def1ef',
    '--card-doc-bg': '#e8edfd',
    '--card-comment-bg': '#fbf0de',
    '--shadow': '0 12px 30px rgba(31,81,132,0.13)',
    '--sidebar-bg': 'linear-gradient(180deg, #143a63 0%, #102c4c 100%)',
    '--sidebar-text': '#eef7ff',
    '--sidebar-muted': 'rgba(238,247,255,0.74)',
    '--sidebar-active': 'rgba(211,156,82,0.18)',
    '--sidebar-card': 'rgba(255,255,255,0.07)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.11)',
    '--sidebar-line': 'rgba(255,255,255,0.09)',
    '--sidebar-faint': 'rgba(238,247,255,0.48)',
    '--sidebar-accent': '#84c7ff',
    '--sidebar-accent-soft': 'rgba(132,199,255,0.2)',
    '--sidebar-accent-glow': 'rgba(132,199,255,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #88d2ff 0%, #2e6fad 100%)',
    '--sidebar-logout': '#ffd6cf',
    '--topbar-bg': 'linear-gradient(180deg, rgba(238,245,251,0.98) 0%, rgba(238,245,251,0.92) 72%, rgba(238,245,251,0.72) 100%)',
    '--topbar-border': '1px solid rgba(20,58,99,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #143a63 0%, #1f5184 45%, #2d7f7a 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(31,81,132,0.2)',
    '--hero-text': '#f4f9ff',
    '--hero-muted': 'rgba(244,249,255,0.82)',
    '--hero-soft': 'rgba(244,249,255,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(8,18,31,0.58)',
    '--neutral-tint': 'rgba(139,161,182,0.12)',
    '--neutral-soft': 'rgba(139,161,182,0.1)',
    '--neutral-border': 'rgba(139,161,182,0.12)',
    '--danger': '#c24d47',
    '--danger-soft': 'rgba(194,77,71,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #f6fbff, #e7f1f8)',
    '--module-navy-tint': 'rgba(31,81,132,0.1)',
    '--module-navy-text': '#1f5184',
    '--module-navy-soft': '#e8f0f8',
    '--module-navy-dark-tint': 'rgba(90,173,235,0.22)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(45,127,122,0.12)',
    '--module-emerald-text': '#296f6b',
    '--module-emerald-soft': '#e6f3f1',
    '--module-emerald-dark-tint': 'rgba(73,184,177,0.22)',
    '--module-emerald-dark-text': '#d1fae5',
    '--module-violet-tint': 'rgba(85,111,176,0.12)',
    '--module-violet-text': '#4f67a2',
    '--module-violet-soft': '#e9eefb',
    '--module-violet-dark-tint': 'rgba(132,162,230,0.22)',
    '--module-violet-dark-text': '#ede9fe',
    '--module-amber-tint': 'rgba(173,119,48,0.12)',
    '--module-amber-text': '#a16c2b',
    '--module-amber-soft': '#f8efdf',
    '--module-amber-dark-tint': 'rgba(221,169,92,0.22)',
    '--module-amber-dark-text': '#fef3c7',
  },
  graphite: {
    id: 'graphite',
    label: 'Walnut Cream',
    '--bg-page': '#f6eedc',
    '--bg-surface': '#fdfbd4',
    '--bg-elev': '#fff8e8',
    '--bg-page-accent': 'radial-gradient(1000px 560px at -8% -12%, rgba(56,36,13,0.16) 0%, rgba(138,63,0,0.14) 38%, rgba(255,255,255,0) 80%)',
    '--text-primary': '#38240D',
    '--text-muted': '#6a5033',
    '--text-soft': '#9a7b56',
    '--border': '#ead8b1',
    '--border-strong': '#d8be8c',
    '--brand': '#8A3F00',
    '--brand-contrast': '#FFF6E8',
    '--accent-gold': '#8A3F00',
    '--accent-emerald': '#7b7250',
    '--accent-violet': '#8b6c4c',
    '--accent-amber': '#8A3F00',
    '--card-user-bg': '#f7edd7',
    '--card-company-bg': '#f4ead2',
    '--card-doc-bg': '#fbf1dd',
    '--card-comment-bg': '#fff0dc',
    '--shadow': '0 14px 30px rgba(56,36,13,0.14)',
    '--sidebar-bg': 'linear-gradient(180deg, #38240D 0%, #241709 100%)',
    '--sidebar-text': '#FDFBD4',
    '--sidebar-muted': 'rgba(253,251,212,0.72)',
    '--sidebar-active': 'rgba(138,63,0,0.22)',
    '--sidebar-card': 'rgba(255,255,255,0.05)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.1)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(253,251,212,0.46)',
    '--sidebar-accent': '#b86a2b',
    '--sidebar-accent-soft': 'rgba(138,63,0,0.22)',
    '--sidebar-accent-glow': 'rgba(138,63,0,0.32)',
    '--sidebar-avatar': 'linear-gradient(135deg, #d8a06a 0%, #8A3F00 100%)',
    '--sidebar-logout': '#ffe2d2',
    '--topbar-bg': 'linear-gradient(180deg, rgba(253,251,212,0.98) 0%, rgba(253,251,212,0.92) 72%, rgba(253,251,212,0.72) 100%)',
    '--topbar-border': '1px solid rgba(138,63,0,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #38240D 0%, #5a3615 40%, #8A3F00 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(56,36,13,0.24)',
    '--hero-text': '#fffde8',
    '--hero-muted': 'rgba(255,253,232,0.82)',
    '--hero-soft': 'rgba(255,253,232,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.1)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.06) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.18)',
    '--overlay-bg': 'rgba(20,12,5,0.6)',
    '--neutral-tint': 'rgba(148,123,97,0.12)',
    '--neutral-soft': 'rgba(148,123,97,0.1)',
    '--neutral-border': 'rgba(148,123,97,0.12)',
    '--danger': '#b84f43',
    '--danger-soft': 'rgba(184,79,67,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #fffbe9, #f6e7c9)',
    '--module-navy-tint': 'rgba(56,36,13,0.08)',
    '--module-navy-text': '#5a3a18',
    '--module-navy-soft': '#f6ead7',
    '--module-navy-dark-tint': 'rgba(138,63,0,0.24)',
    '--module-navy-dark-text': '#fff1dd',
    '--module-emerald-tint': 'rgba(123,114,80,0.12)',
    '--module-emerald-text': '#6b6343',
    '--module-emerald-soft': '#f3eedf',
    '--module-emerald-dark-tint': 'rgba(166,145,96,0.22)',
    '--module-emerald-dark-text': '#dcfce7',
    '--module-violet-tint': 'rgba(139,108,76,0.12)',
    '--module-violet-text': '#7b5f43',
    '--module-violet-soft': '#f7efe5',
    '--module-violet-dark-tint': 'rgba(138,63,0,0.2)',
    '--module-violet-dark-text': '#fff1e3',
    '--module-amber-tint': 'rgba(138,63,0,0.14)',
    '--module-amber-text': '#8A3F00',
    '--module-amber-soft': '#fff0df',
    '--module-amber-dark-tint': 'rgba(255,179,107,0.26)',
    '--module-amber-dark-text': '#fff1cf',
  },
  modern_action: {
    id: 'modern_action',
    label: 'Modern Action',
    '--bg-page': '#eef2f7',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#f7f9fc',
    '--bg-page-accent': 'radial-gradient(1050px 560px at -8% -10%, rgba(22,56,96,0.18) 0%, rgba(245,137,52,0.12) 36%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#15273a',
    '--text-muted': '#667588',
    '--text-soft': '#94a0ad',
    '--border': '#dce5ef',
    '--border-strong': '#ced8e4',
    '--brand': '#163860',
    '--brand-contrast': '#f7fbff',
    '--accent-gold': '#f18a3a',
    '--accent-emerald': '#2f8f7c',
    '--accent-violet': '#5d74c9',
    '--accent-amber': '#d96d1f',
    '--card-user-bg': '#e5eefb',
    '--card-company-bg': '#e4f5f1',
    '--card-doc-bg': '#ebeffd',
    '--card-comment-bg': '#fff0e4',
    '--shadow': '0 14px 30px rgba(22,56,96,0.12)',
    '--sidebar-bg': 'linear-gradient(180deg, #132f52 0%, #0f2440 100%)',
    '--sidebar-text': '#f3f7fc',
    '--sidebar-muted': 'rgba(243,247,252,0.74)',
    '--sidebar-active': 'rgba(241,138,58,0.18)',
    '--sidebar-card': 'rgba(255,255,255,0.07)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.12)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(243,247,252,0.48)',
    '--sidebar-accent': '#ffad66',
    '--sidebar-accent-soft': 'rgba(255,173,102,0.22)',
    '--sidebar-accent-glow': 'rgba(255,173,102,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #ffb56a 0%, #d96d1f 100%)',
    '--sidebar-logout': '#ffd5c5',
    '--topbar-bg': 'linear-gradient(180deg, rgba(238,242,247,0.98) 0%, rgba(238,242,247,0.92) 72%, rgba(238,242,247,0.72) 100%)',
    '--topbar-border': '1px solid rgba(19,47,82,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #163860 0%, #1e4d82 45%, #ef7d32 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(22,56,96,0.2)',
    '--hero-text': '#f7fbff',
    '--hero-muted': 'rgba(247,251,255,0.82)',
    '--hero-soft': 'rgba(247,251,255,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(8,16,28,0.58)',
    '--neutral-tint': 'rgba(148,163,184,0.12)',
    '--neutral-soft': 'rgba(148,163,184,0.1)',
    '--neutral-border': 'rgba(148,163,184,0.12)',
    '--danger': '#c8513b',
    '--danger-soft': 'rgba(200,81,59,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #f8fbff, #edf3f9)',
    '--module-navy-tint': 'rgba(22,56,96,0.1)',
    '--module-navy-text': '#173e69',
    '--module-navy-soft': '#eaf1fb',
    '--module-navy-dark-tint': 'rgba(71,134,219,0.22)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(47,143,124,0.12)',
    '--module-emerald-text': '#2c7e6d',
    '--module-emerald-soft': '#e7f6f2',
    '--module-emerald-dark-tint': 'rgba(63,190,166,0.22)',
    '--module-emerald-dark-text': '#d1fae5',
    '--module-violet-tint': 'rgba(93,116,201,0.12)',
    '--module-violet-text': '#586fc0',
    '--module-violet-soft': '#edf0fd',
    '--module-violet-dark-tint': 'rgba(137,156,236,0.22)',
    '--module-violet-dark-text': '#ede9fe',
    '--module-amber-tint': 'rgba(241,138,58,0.12)',
    '--module-amber-text': '#d96d1f',
    '--module-amber-soft': '#fff1e7',
    '--module-amber-dark-tint': 'rgba(255,173,102,0.24)',
    '--module-amber-dark-text': '#fff0db',
  },
  clean_productivity: {
    id: 'clean_productivity',
    label: 'Clean Productivity',
    '--bg-page': '#f2f7f7',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#f7fbfb',
    '--bg-page-accent': 'radial-gradient(980px 540px at -8% -10%, rgba(34,140,140,0.14) 0%, rgba(113,132,150,0.1) 40%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#21343b',
    '--text-muted': '#687b83',
    '--text-soft': '#92a2a9',
    '--border': '#dce7e8',
    '--border-strong': '#cfdcde',
    '--brand': '#227b7d',
    '--brand-contrast': '#f6fbfb',
    '--accent-gold': '#c6904c',
    '--accent-emerald': '#2d8d85',
    '--accent-violet': '#6685a8',
    '--accent-amber': '#a86e2f',
    '--card-user-bg': '#e6f1f4',
    '--card-company-bg': '#e3f4f1',
    '--card-doc-bg': '#edf3f7',
    '--card-comment-bg': '#f8efe3',
    '--shadow': '0 12px 28px rgba(34,123,125,0.1)',
    '--sidebar-bg': 'linear-gradient(180deg, #245c60 0%, #1f4d50 100%)',
    '--sidebar-text': '#eff8f8',
    '--sidebar-muted': 'rgba(239,248,248,0.74)',
    '--sidebar-active': 'rgba(130,194,188,0.18)',
    '--sidebar-card': 'rgba(255,255,255,0.07)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.12)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(239,248,248,0.48)',
    '--sidebar-accent': '#8fd5cf',
    '--sidebar-accent-soft': 'rgba(143,213,207,0.22)',
    '--sidebar-accent-glow': 'rgba(143,213,207,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #8fd5cf 0%, #3d8b8e 100%)',
    '--sidebar-logout': '#ffd8cd',
    '--topbar-bg': 'linear-gradient(180deg, rgba(242,247,247,0.98) 0%, rgba(242,247,247,0.92) 72%, rgba(242,247,247,0.72) 100%)',
    '--topbar-border': '1px solid rgba(36,92,96,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #245c60 0%, #2d8d85 46%, #8da3ab 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(34,123,125,0.18)',
    '--hero-text': '#f5fbfb',
    '--hero-muted': 'rgba(245,251,251,0.82)',
    '--hero-soft': 'rgba(245,251,251,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(9,18,20,0.54)',
    '--neutral-tint': 'rgba(146,165,171,0.12)',
    '--neutral-soft': 'rgba(146,165,171,0.1)',
    '--neutral-border': 'rgba(146,165,171,0.12)',
    '--danger': '#c45c4a',
    '--danger-soft': 'rgba(196,92,74,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #fbfefe, #edf5f5)',
    '--module-navy-tint': 'rgba(102,133,168,0.1)',
    '--module-navy-text': '#587493',
    '--module-navy-soft': '#eef3f8',
    '--module-navy-dark-tint': 'rgba(130,166,206,0.22)',
    '--module-navy-dark-text': '#e0ebf8',
    '--module-emerald-tint': 'rgba(45,141,133,0.12)',
    '--module-emerald-text': '#2b7f79',
    '--module-emerald-soft': '#e8f6f4',
    '--module-emerald-dark-tint': 'rgba(81,188,177,0.22)',
    '--module-emerald-dark-text': '#d6fbf4',
    '--module-violet-tint': 'rgba(113,132,150,0.12)',
    '--module-violet-text': '#66788b',
    '--module-violet-soft': '#f0f3f6',
    '--module-violet-dark-tint': 'rgba(151,170,188,0.22)',
    '--module-violet-dark-text': '#eef2f7',
    '--module-amber-tint': 'rgba(198,144,76,0.12)',
    '--module-amber-text': '#a97534',
    '--module-amber-soft': '#f9f1e6',
    '--module-amber-dark-tint': 'rgba(223,177,118,0.22)',
    '--module-amber-dark-text': '#fff1dc',
  },
  kpi_focus: {
    id: 'kpi_focus',
    label: 'KPI Focus',
    '--bg-page': '#0d1110',
    '--bg-surface': '#141918',
    '--bg-elev': '#101413',
    '--bg-page-accent': 'radial-gradient(1100px 580px at -10% -10%, rgba(83,205,116,0.12) 0%, rgba(28,42,35,0.12) 42%, rgba(0,0,0,0) 78%)',
    '--text-primary': '#ecf6ef',
    '--text-muted': '#b1c2b6',
    '--text-soft': '#81938a',
    '--border': '#1f2a26',
    '--border-strong': '#27342f',
    '--brand': '#57cf75',
    '--brand-contrast': '#08100b',
    '--accent-gold': '#c7d98a',
    '--accent-emerald': '#57cf75',
    '--accent-violet': '#75a9a1',
    '--accent-amber': '#9ed864',
    '--card-user-bg': 'rgba(87,207,117,0.08)',
    '--card-company-bg': 'rgba(74,156,118,0.12)',
    '--card-doc-bg': 'rgba(94,119,138,0.12)',
    '--card-comment-bg': 'rgba(158,216,100,0.12)',
    '--shadow': '0 14px 32px rgba(0,0,0,0.34)',
    '--sidebar-bg': 'linear-gradient(180deg, #0b0f0f 0%, #080b0b 100%)',
    '--sidebar-text': '#effaf2',
    '--sidebar-muted': 'rgba(239,250,242,0.72)',
    '--sidebar-active': 'rgba(87,207,117,0.16)',
    '--sidebar-card': 'rgba(255,255,255,0.04)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.08)',
    '--sidebar-line': 'rgba(255,255,255,0.06)',
    '--sidebar-faint': 'rgba(239,250,242,0.42)',
    '--sidebar-accent': '#7fe493',
    '--sidebar-accent-soft': 'rgba(127,228,147,0.18)',
    '--sidebar-accent-glow': 'rgba(127,228,147,0.26)',
    '--sidebar-avatar': 'linear-gradient(135deg, #82e597 0%, #2f8650 100%)',
    '--sidebar-logout': '#ffc6c6',
    '--topbar-bg': 'linear-gradient(180deg, rgba(11,15,15,0.98) 0%, rgba(8,11,11,0.92) 72%, rgba(8,11,11,0.74) 100%)',
    '--topbar-border': '1px solid rgba(87,207,117,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #101615 0%, #19211e 38%, #24352d 68%, #57cf75 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(0,0,0,0.3)',
    '--hero-text': '#f3fff5',
    '--hero-muted': 'rgba(243,255,245,0.82)',
    '--hero-soft': 'rgba(243,255,245,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.1)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.04) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.16)',
    '--overlay-bg': 'rgba(2,5,3,0.74)',
    '--neutral-tint': 'rgba(129,147,138,0.12)',
    '--neutral-soft': 'rgba(129,147,138,0.08)',
    '--neutral-border': 'rgba(129,147,138,0.14)',
    '--danger': '#ff7f7f',
    '--danger-soft': 'rgba(255,127,127,0.16)',
    '--danger-contrast': '#170a0a',
    '--preview-bg': 'linear-gradient(180deg, #121917, #0d1210)',
    '--module-navy-tint': 'rgba(110,170,198,0.18)',
    '--module-navy-text': '#d6edf6',
    '--module-navy-soft': 'rgba(110,170,198,0.12)',
    '--module-navy-dark-tint': 'rgba(110,170,198,0.24)',
    '--module-navy-dark-text': '#e5f5fb',
    '--module-emerald-tint': 'rgba(87,207,117,0.18)',
    '--module-emerald-text': '#ddffe3',
    '--module-emerald-soft': 'rgba(87,207,117,0.12)',
    '--module-emerald-dark-tint': 'rgba(87,207,117,0.24)',
    '--module-emerald-dark-text': '#ecfff0',
    '--module-violet-tint': 'rgba(125,143,168,0.18)',
    '--module-violet-text': '#e3ebf6',
    '--module-violet-soft': 'rgba(125,143,168,0.12)',
    '--module-violet-dark-tint': 'rgba(125,143,168,0.24)',
    '--module-violet-dark-text': '#eef4fb',
    '--module-amber-tint': 'rgba(158,216,100,0.18)',
    '--module-amber-text': '#eefcdc',
    '--module-amber-soft': 'rgba(158,216,100,0.12)',
    '--module-amber-dark-tint': 'rgba(158,216,100,0.24)',
    '--module-amber-dark-text': '#f4ffe8',
  },
  mono: {
    id: 'mono',
    label: 'Mono Contrast',
    '--bg-page': '#f3f3f3',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#f7f7f7',
    '--bg-page-accent': 'radial-gradient(980px 540px at -8% -10%, rgba(0,0,0,0.12) 0%, rgba(115,115,115,0.08) 40%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#0a0a0a',
    '--text-muted': '#4b4b4b',
    '--text-soft': '#777777',
    '--border': '#dddddd',
    '--border-strong': '#cfcfcf',
    '--brand': '#000000',
    '--brand-contrast': '#ffffff',
    '--accent-gold': '#1a1a1a',
    '--accent-emerald': '#3a3a3a',
    '--accent-violet': '#5c5c5c',
    '--accent-amber': '#7a7a7a',
    '--card-user-bg': '#efefef',
    '--card-company-bg': '#ececec',
    '--card-doc-bg': '#f2f2f2',
    '--card-comment-bg': '#e9e9e9',
    '--shadow': '0 12px 28px rgba(0,0,0,0.08)',
    '--sidebar-bg': 'linear-gradient(180deg, #050505 0%, #111111 100%)',
    '--sidebar-text': '#ffffff',
    '--sidebar-muted': 'rgba(255,255,255,0.72)',
    '--sidebar-active': 'rgba(255,255,255,0.12)',
    '--sidebar-card': 'rgba(255,255,255,0.05)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.1)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(255,255,255,0.42)',
    '--sidebar-accent': '#ffffff',
    '--sidebar-accent-soft': 'rgba(255,255,255,0.18)',
    '--sidebar-accent-glow': 'rgba(255,255,255,0.24)',
    '--sidebar-avatar': 'linear-gradient(135deg, #ffffff 0%, #6d6d6d 100%)',
    '--sidebar-logout': '#f0f0f0',
    '--topbar-bg': 'linear-gradient(180deg, rgba(243,243,243,0.98) 0%, rgba(243,243,243,0.92) 72%, rgba(243,243,243,0.72) 100%)',
    '--topbar-border': '1px solid rgba(0,0,0,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #000000 0%, #1a1a1a 48%, #4a4a4a 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(0,0,0,0.18)',
    '--hero-text': '#ffffff',
    '--hero-muted': 'rgba(255,255,255,0.82)',
    '--hero-soft': 'rgba(255,255,255,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.1)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.06) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.18)',
    '--overlay-bg': 'rgba(0,0,0,0.62)',
    '--neutral-tint': 'rgba(115,115,115,0.12)',
    '--neutral-soft': 'rgba(115,115,115,0.1)',
    '--neutral-border': 'rgba(115,115,115,0.14)',
    '--danger': '#6b0000',
    '--danger-soft': 'rgba(107,0,0,0.1)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #fcfcfc, #efefef)',
    '--module-navy-tint': 'rgba(0,0,0,0.08)',
    '--module-navy-text': '#111111',
    '--module-navy-soft': '#f1f1f1',
    '--module-navy-dark-tint': 'rgba(255,255,255,0.18)',
    '--module-navy-dark-text': '#ffffff',
    '--module-emerald-tint': 'rgba(45,45,45,0.1)',
    '--module-emerald-text': '#222222',
    '--module-emerald-soft': '#f3f3f3',
    '--module-emerald-dark-tint': 'rgba(255,255,255,0.16)',
    '--module-emerald-dark-text': '#ffffff',
    '--module-violet-tint': 'rgba(85,85,85,0.1)',
    '--module-violet-text': '#363636',
    '--module-violet-soft': '#f5f5f5',
    '--module-violet-dark-tint': 'rgba(255,255,255,0.14)',
    '--module-violet-dark-text': '#ffffff',
    '--module-amber-tint': 'rgba(115,115,115,0.12)',
    '--module-amber-text': '#4a4a4a',
    '--module-amber-soft': '#f0f0f0',
    '--module-amber-dark-tint': 'rgba(255,255,255,0.12)',
    '--module-amber-dark-text': '#ffffff',
  },
  modern_fintech: {
    id: 'modern_fintech',
    label: 'Modern Fintech',
    '--bg-page': '#F1F5F9',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#f8fafc',
    '--bg-page-accent': 'radial-gradient(1050px 560px at -8% -10%, rgba(13,148,136,0.1) 0%, rgba(59,130,246,0.08) 38%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#0F172A',
    '--text-muted': '#4b5768',
    '--text-soft': '#8492a6',
    '--border': '#dde4ed',
    '--border-strong': '#ccd6e3',
    '--brand': '#0D9488',
    '--brand-contrast': '#f0fdfa',
    '--accent-gold': '#c6902f',
    '--accent-emerald': '#0D9488',
    '--accent-violet': '#3B82F6',
    '--accent-amber': '#b9700f',
    '--card-user-bg': '#e6effa',
    '--card-company-bg': '#dcf4ef',
    '--card-doc-bg': '#e6edfd',
    '--card-comment-bg': '#fbf1e0',
    '--shadow': '0 12px 28px rgba(15,23,42,0.1)',
    '--sidebar-bg': 'linear-gradient(180deg, #1E1E24 0%, #17171c 100%)',
    '--sidebar-text': '#f4f6f8',
    '--sidebar-muted': 'rgba(244,246,248,0.68)',
    '--sidebar-active': 'rgba(13,148,136,0.22)',
    '--sidebar-card': 'rgba(255,255,255,0.05)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.09)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(244,246,248,0.42)',
    '--sidebar-accent': '#2dd4c4',
    '--sidebar-accent-soft': 'rgba(45,212,196,0.18)',
    '--sidebar-accent-glow': 'rgba(45,212,196,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #3B82F6 0%, #0D9488 100%)',
    '--sidebar-logout': '#ff9d9d',
    '--topbar-bg': 'linear-gradient(180deg, rgba(241,245,249,0.98) 0%, rgba(241,245,249,0.92) 72%, rgba(241,245,249,0.72) 100%)',
    '--topbar-border': '1px solid rgba(15,23,42,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #1E1E24 0%, #124e49 48%, #0D9488 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(15,23,42,0.22)',
    '--hero-text': '#f4fbfa',
    '--hero-muted': 'rgba(244,251,250,0.82)',
    '--hero-soft': 'rgba(244,251,250,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.07) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.07) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(8,10,14,0.6)',
    '--neutral-tint': 'rgba(75,87,104,0.1)',
    '--neutral-soft': 'rgba(75,87,104,0.08)',
    '--neutral-border': 'rgba(75,87,104,0.12)',
    '--danger': '#dc2626',
    '--danger-soft': 'rgba(220,38,38,0.1)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #f8fafc, #eaf1f7)',
    '--module-navy-tint': 'rgba(59,130,246,0.1)',
    '--module-navy-text': '#1d4ed8',
    '--module-navy-soft': '#e6efff',
    '--module-navy-dark-tint': 'rgba(59,130,246,0.24)',
    '--module-navy-dark-text': '#dbe9ff',
    '--module-emerald-tint': 'rgba(13,148,136,0.1)',
    '--module-emerald-text': '#0f766e',
    '--module-emerald-soft': '#dff5f2',
    '--module-emerald-dark-tint': 'rgba(45,212,196,0.24)',
    '--module-emerald-dark-text': '#ccfbf1',
    '--module-violet-tint': 'rgba(59,130,246,0.1)',
    '--module-violet-text': '#2563eb',
    '--module-violet-soft': '#e8f0ff',
    '--module-violet-dark-tint': 'rgba(96,165,250,0.24)',
    '--module-violet-dark-text': '#dbeafe',
    '--module-amber-tint': 'rgba(185,112,15,0.1)',
    '--module-amber-text': '#92600c',
    '--module-amber-soft': '#faf0dc',
    '--module-amber-dark-tint': 'rgba(217,159,61,0.24)',
    '--module-amber-dark-text': '#fdecc8',
  },
  platinum_ledger: {
    id: 'platinum_ledger',
    label: 'Platinum Ledger',
    '--bg-page': '#F8FAFC',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#fbfcfd',
    '--bg-page-accent': 'radial-gradient(1050px 560px at -8% -10%, rgba(37,99,235,0.08) 0%, rgba(15,23,42,0.05) 40%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#0F172A',
    '--text-muted': '#526079',
    '--text-soft': '#8996ab',
    '--border': '#e2e8f0',
    '--border-strong': '#d3dce8',
    '--brand': '#0F172A',
    '--brand-contrast': '#ffffff',
    '--accent-gold': '#b5872f',
    '--accent-emerald': '#0f766e',
    '--accent-violet': '#2563EB',
    '--accent-amber': '#a8620f',
    '--card-user-bg': '#F1F5F9',
    '--card-company-bg': '#F1F5F9',
    '--card-doc-bg': '#F1F5F9',
    '--card-comment-bg': '#F1F5F9',
    '--shadow': '0 10px 24px rgba(15,23,42,0.07)',
    '--sidebar-bg': 'linear-gradient(180deg, #0F172A 0%, #0a111f 100%)',
    '--sidebar-text': '#f4f7fb',
    '--sidebar-muted': 'rgba(244,247,251,0.68)',
    '--sidebar-active': 'rgba(37,99,235,0.2)',
    '--sidebar-card': 'rgba(255,255,255,0.05)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.09)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(244,247,251,0.42)',
    '--sidebar-accent': '#3b82f6',
    '--sidebar-accent-soft': 'rgba(59,130,246,0.18)',
    '--sidebar-accent-glow': 'rgba(59,130,246,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #60a5fa 0%, #2563EB 100%)',
    '--sidebar-logout': '#ff9d9d',
    '--topbar-bg': 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(248,250,252,0.92) 72%, rgba(248,250,252,0.72) 100%)',
    '--topbar-border': '1px solid rgba(15,23,42,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(15,23,42,0.2)',
    '--hero-text': '#ffffff',
    '--hero-muted': 'rgba(255,255,255,0.8)',
    '--hero-soft': 'rgba(255,255,255,0.64)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.07) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.07) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(8,11,18,0.56)',
    '--neutral-tint': 'rgba(82,96,121,0.1)',
    '--neutral-soft': 'rgba(82,96,121,0.08)',
    '--neutral-border': 'rgba(82,96,121,0.12)',
    '--danger': '#dc2626',
    '--danger-soft': 'rgba(220,38,38,0.1)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #fbfcfd, #eef2f7)',
    '--module-navy-tint': 'rgba(37,99,235,0.1)',
    '--module-navy-text': '#1d4ed8',
    '--module-navy-soft': '#e6efff',
    '--module-navy-dark-tint': 'rgba(59,130,246,0.24)',
    '--module-navy-dark-text': '#dbe9ff',
    '--module-emerald-tint': 'rgba(15,118,110,0.1)',
    '--module-emerald-text': '#0f766e',
    '--module-emerald-soft': '#e1f4f2',
    '--module-emerald-dark-tint': 'rgba(45,212,196,0.24)',
    '--module-emerald-dark-text': '#ccfbf1',
    '--module-violet-tint': 'rgba(37,99,235,0.1)',
    '--module-violet-text': '#2563EB',
    '--module-violet-soft': '#e8f0ff',
    '--module-violet-dark-tint': 'rgba(96,165,250,0.24)',
    '--module-violet-dark-text': '#dbeafe',
    '--module-amber-tint': 'rgba(168,98,15,0.1)',
    '--module-amber-text': '#8a520c',
    '--module-amber-soft': '#f7ecd9',
    '--module-amber-dark-tint': 'rgba(217,159,61,0.24)',
    '--module-amber-dark-text': '#fdecc8',
  },
  midnight_auditor: {
    id: 'midnight_auditor',
    label: 'Midnight Auditor',
    '--bg-page': '#111827',
    '--bg-surface': '#1F2937',
    '--bg-elev': '#182030',
    '--bg-page-accent': 'radial-gradient(1100px 600px at -8% -10%, rgba(20,184,166,0.14) 0%, rgba(168,85,247,0.1) 40%, rgba(0,0,0,0) 78%)',
    '--text-primary': '#f1f5f9',
    '--text-muted': '#a7b2c3',
    '--text-soft': '#7e8ba0',
    '--border': '#2a3648',
    '--border-strong': '#354258',
    '--brand': '#14B8A6',
    '--brand-contrast': '#052e2b',
    '--accent-gold': '#d2b267',
    '--accent-emerald': '#14B8A6',
    '--accent-violet': '#A855F7',
    '--accent-amber': '#d6a45a',
    '--card-user-bg': '#1F2937',
    '--card-company-bg': '#1F2937',
    '--card-doc-bg': '#1F2937',
    '--card-comment-bg': '#1F2937',
    '--shadow': '0 14px 28px rgba(0,0,0,0.4)',
    '--sidebar-bg': 'linear-gradient(180deg, #0B0F19 0%, #080b13 100%)',
    '--sidebar-text': '#eef2f8',
    '--sidebar-muted': 'rgba(238,242,248,0.68)',
    '--sidebar-active': 'rgba(168,85,247,0.2)',
    '--sidebar-card': 'rgba(255,255,255,0.05)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.08)',
    '--sidebar-line': 'rgba(255,255,255,0.08)',
    '--sidebar-faint': 'rgba(238,242,248,0.42)',
    '--sidebar-accent': '#c084fc',
    '--sidebar-accent-soft': 'rgba(192,132,252,0.18)',
    '--sidebar-accent-glow': 'rgba(192,132,252,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #c084fc 0%, #A855F7 100%)',
    '--sidebar-logout': '#ffb2b2',
    '--topbar-bg': 'linear-gradient(180deg, rgba(17,24,39,0.98) 0%, rgba(15,21,34,0.92) 72%, rgba(15,21,34,0.72) 100%)',
    '--topbar-border': '1px solid rgba(255,255,255,0.08)',
    '--hero-bg': 'linear-gradient(135deg, #115E59 0%, #0F766E 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(0,0,0,0.32)',
    '--hero-text': '#f2fbfa',
    '--hero-muted': 'rgba(242,251,250,0.8)',
    '--hero-soft': 'rgba(242,251,250,0.64)',
    '--hero-role-bg': 'rgba(255,255,255,0.1)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.04) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.16)',
    '--overlay-bg': 'rgba(3,5,9,0.74)',
    '--neutral-tint': 'rgba(167,178,195,0.14)',
    '--neutral-soft': 'rgba(167,178,195,0.1)',
    '--neutral-border': 'rgba(167,178,195,0.16)',
    '--danger': '#ff7878',
    '--danger-soft': 'rgba(255,120,120,0.16)',
    '--danger-contrast': '#14090a',
    '--preview-bg': 'linear-gradient(180deg, #182030, #111827)',
    '--module-navy-tint': 'rgba(96,165,250,0.2)',
    '--module-navy-text': '#dbeafe',
    '--module-navy-soft': 'rgba(96,165,250,0.14)',
    '--module-navy-dark-tint': 'rgba(59,130,246,0.28)',
    '--module-navy-dark-text': '#dbeafe',
    '--module-emerald-tint': 'rgba(20,184,166,0.2)',
    '--module-emerald-text': '#ccfbf1',
    '--module-emerald-soft': 'rgba(20,184,166,0.14)',
    '--module-emerald-dark-tint': 'rgba(20,184,166,0.28)',
    '--module-emerald-dark-text': '#ccfbf1',
    '--module-violet-tint': 'rgba(168,85,247,0.2)',
    '--module-violet-text': '#f3e8ff',
    '--module-violet-soft': 'rgba(168,85,247,0.14)',
    '--module-violet-dark-tint': 'rgba(192,132,252,0.28)',
    '--module-violet-dark-text': '#f3e8ff',
    '--module-amber-tint': 'rgba(251,191,36,0.18)',
    '--module-amber-text': '#fef3c7',
    '--module-amber-soft': 'rgba(245,158,11,0.14)',
    '--module-amber-dark-tint': 'rgba(245,158,11,0.28)',
    '--module-amber-dark-text': '#fef3c7',
  },
  emerald_asset: {
    id: 'emerald_asset',
    label: 'Emerald Asset',
    '--bg-page': '#F4F7F6',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#f9fbfa',
    '--bg-page-accent': 'radial-gradient(1050px 560px at -8% -10%, rgba(5,150,105,0.09) 0%, rgba(6,78,59,0.06) 40%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#0d271f',
    '--text-muted': '#4c655c',
    '--text-soft': '#84988f',
    '--border': '#dce8e3',
    '--border-strong': '#cbdbd4',
    '--brand': '#064E3B',
    '--brand-contrast': '#ffffff',
    '--accent-gold': '#b5872f',
    '--accent-emerald': '#059669',
    '--accent-violet': '#5d6b8f',
    '--accent-amber': '#a8620f',
    '--card-user-bg': '#ffffff',
    '--card-company-bg': '#ffffff',
    '--card-doc-bg': '#ffffff',
    '--card-comment-bg': '#ffffff',
    '--shadow': '0 10px 24px rgba(6,78,59,0.08)',
    '--sidebar-bg': 'linear-gradient(180deg, #064E3B 0%, #05392c 100%)',
    '--sidebar-text': '#f2f9f6',
    '--sidebar-muted': 'rgba(242,249,246,0.7)',
    '--sidebar-active': 'rgba(5,150,105,0.22)',
    '--sidebar-card': 'rgba(255,255,255,0.06)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.1)',
    '--sidebar-line': 'rgba(255,255,255,0.09)',
    '--sidebar-faint': 'rgba(242,249,246,0.46)',
    '--sidebar-accent': '#34d399',
    '--sidebar-accent-soft': 'rgba(52,211,153,0.2)',
    '--sidebar-accent-glow': 'rgba(52,211,153,0.28)',
    '--sidebar-avatar': 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
    '--sidebar-logout': '#ffb4b4',
    '--topbar-bg': 'linear-gradient(180deg, rgba(244,247,246,0.98) 0%, rgba(244,247,246,0.92) 72%, rgba(244,247,246,0.72) 100%)',
    '--topbar-border': '1px solid rgba(6,78,59,0.1)',
    '--hero-bg': 'linear-gradient(135deg, #022C22 0%, #033d30 100%)',
    '--hero-border': 'rgba(255,255,255,0.08)',
    '--hero-shadow': '0 24px 54px rgba(2,44,34,0.28)',
    '--hero-text': '#ffffff',
    '--hero-muted': 'rgba(255,255,255,0.82)',
    '--hero-soft': 'rgba(255,255,255,0.66)',
    '--hero-role-bg': 'rgba(255,255,255,0.12)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.07) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.07) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.2)',
    '--overlay-bg': 'rgba(4,16,12,0.56)',
    '--neutral-tint': 'rgba(76,101,92,0.1)',
    '--neutral-soft': 'rgba(76,101,92,0.08)',
    '--neutral-border': 'rgba(76,101,92,0.12)',
    '--danger': '#c0362f',
    '--danger-soft': 'rgba(192,54,47,0.1)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #f9fbfa, #e9f2ee)',
    '--module-navy-tint': 'rgba(93,107,143,0.1)',
    '--module-navy-text': '#4a5a83',
    '--module-navy-soft': '#eaeef6',
    '--module-navy-dark-tint': 'rgba(129,153,220,0.24)',
    '--module-navy-dark-text': '#dde5f7',
    '--module-emerald-tint': 'rgba(5,150,105,0.1)',
    '--module-emerald-text': '#047551',
    '--module-emerald-soft': '#e0f3ea',
    '--module-emerald-dark-tint': 'rgba(52,211,153,0.24)',
    '--module-emerald-dark-text': '#d1fae5',
    '--module-violet-tint': 'rgba(93,107,143,0.1)',
    '--module-violet-text': '#4a5a83',
    '--module-violet-soft': '#eaeef6',
    '--module-violet-dark-tint': 'rgba(129,153,220,0.24)',
    '--module-violet-dark-text': '#dde5f7',
    '--module-amber-tint': 'rgba(168,98,15,0.1)',
    '--module-amber-text': '#8a520c',
    '--module-amber-soft': '#f7ecd9',
    '--module-amber-dark-tint': 'rgba(217,159,61,0.24)',
    '--module-amber-dark-text': '#fdecc8',
  },
  golden_hour: {
    id: 'golden_hour',
    label: 'Golden Hour',
    '--bg-page': '#FFFBEB',
    '--bg-surface': '#ffffff',
    '--bg-elev': '#fffdf5',
    '--bg-page-accent': 'radial-gradient(1050px 560px at -8% -10%, rgba(234,179,8,0.14) 0%, rgba(217,119,6,0.08) 40%, rgba(255,255,255,0) 78%)',
    '--text-primary': '#3f2d05',
    '--text-muted': '#7a6a3f',
    '--text-soft': '#a6976c',
    '--border': '#f2e2ad',
    '--border-strong': '#e8d287',
    '--brand': '#B45309',
    '--brand-contrast': '#fffbeb',
    '--accent-gold': '#EAB308',
    '--accent-emerald': '#3d8f6d',
    '--accent-violet': '#8b6b3d',
    '--accent-amber': '#D97706',
    '--card-user-bg': '#fef3c7',
    '--card-company-bg': '#fdf0d5',
    '--card-doc-bg': '#fef8e3',
    '--card-comment-bg': '#fdf3d8',
    '--shadow': '0 12px 28px rgba(180,83,9,0.12)',
    '--sidebar-bg': 'linear-gradient(180deg, #3F2D05 0%, #2a1e03 100%)',
    '--sidebar-text': '#fdf6e3',
    '--sidebar-muted': 'rgba(253,246,227,0.72)',
    '--sidebar-active': 'rgba(234,179,8,0.22)',
    '--sidebar-card': 'rgba(255,255,255,0.06)',
    '--sidebar-card-strong': 'rgba(255,255,255,0.1)',
    '--sidebar-line': 'rgba(255,255,255,0.09)',
    '--sidebar-faint': 'rgba(253,246,227,0.46)',
    '--sidebar-accent': '#facc15',
    '--sidebar-accent-soft': 'rgba(250,204,21,0.2)',
    '--sidebar-accent-glow': 'rgba(250,204,21,0.3)',
    '--sidebar-avatar': 'linear-gradient(135deg, #facc15 0%, #D97706 100%)',
    '--sidebar-logout': '#ffb4a8',
    '--topbar-bg': 'linear-gradient(180deg, rgba(255,251,235,0.98) 0%, rgba(255,251,235,0.92) 72%, rgba(255,251,235,0.72) 100%)',
    '--topbar-border': '1px solid rgba(180,83,9,0.12)',
    '--hero-bg': 'linear-gradient(135deg, #92400E 0%, #B45309 46%, #EAB308 100%)',
    '--hero-border': 'rgba(255,255,255,0.1)',
    '--hero-shadow': '0 24px 54px rgba(146,64,14,0.28)',
    '--hero-text': '#fffbeb',
    '--hero-muted': 'rgba(255,251,235,0.86)',
    '--hero-soft': 'rgba(255,251,235,0.68)',
    '--hero-role-bg': 'rgba(255,255,255,0.16)',
    '--hero-status-bg': 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.09) 100%)',
    '--hero-action-wrap-bg': 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.09) 100%)',
    '--hero-action-border': 'rgba(255,255,255,0.24)',
    '--overlay-bg': 'rgba(24,15,2,0.56)',
    '--neutral-tint': 'rgba(122,106,63,0.12)',
    '--neutral-soft': 'rgba(122,106,63,0.1)',
    '--neutral-border': 'rgba(122,106,63,0.14)',
    '--danger': '#c0362f',
    '--danger-soft': 'rgba(192,54,47,0.12)',
    '--danger-contrast': '#ffffff',
    '--preview-bg': 'linear-gradient(180deg, #fffdf5, #fbedc0)',
    '--module-navy-tint': 'rgba(139,107,61,0.1)',
    '--module-navy-text': '#6b5228',
    '--module-navy-soft': '#f3ecd8',
    '--module-navy-dark-tint': 'rgba(217,159,61,0.24)',
    '--module-navy-dark-text': '#fdecc8',
    '--module-emerald-tint': 'rgba(61,143,109,0.12)',
    '--module-emerald-text': '#2f6b50',
    '--module-emerald-soft': '#e5f3ec',
    '--module-emerald-dark-tint': 'rgba(74,196,153,0.24)',
    '--module-emerald-dark-text': '#d1fae5',
    '--module-violet-tint': 'rgba(139,107,61,0.1)',
    '--module-violet-text': '#6b5228',
    '--module-violet-soft': '#f3ecd8',
    '--module-violet-dark-tint': 'rgba(217,159,61,0.24)',
    '--module-violet-dark-text': '#fdecc8',
    '--module-amber-tint': 'rgba(217,119,6,0.14)',
    '--module-amber-text': '#92400E',
    '--module-amber-soft': '#fdf0d0',
    '--module-amber-dark-tint': 'rgba(250,204,21,0.28)',
    '--module-amber-dark-text': '#fef9c3',
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    const vars = THEMES[theme] || THEMES.light;
    Object.entries(vars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });
    const darkThemes = ['dark', 'kpi_focus', 'midnight_auditor'];
    document.documentElement.setAttribute('data-color-scheme', darkThemes.includes(theme) ? 'dark' : 'light');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const themeOptions = useMemo(
    () => Object.values(THEMES).map(({ id, label }) => ({ id, label })),
    []
  );

  const cycleTheme = useCallback(() => {
    setTheme((current) => {
      const ids = themeOptions.map((option) => option.id);
      const currentIndex = ids.indexOf(current);
      return ids[(currentIndex + 1) % ids.length];
    });
  }, [themeOptions]);

  const value = useMemo(
    () => ({ theme, setTheme, cycleTheme, themeOptions }),
    [theme, cycleTheme, themeOptions]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
